import React, { useState, useCallback, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Moment from "moment";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import showErrorToast from "../../components/CustomComponents/toast/toast";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import useThemeColor from "../../hooks/useThemeColor";
import useDarkMode from "../../hooks/useDarkMode";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import "./zReport.css";
import "../order/ManageOrderFilters.css";

const reconId = (row) => row.stockReconciliation_ID ?? row.stockReconciliationId ?? row.StockReconciliation_ID;

const sortByOptions = [
  { value: "CreatedAt", label: "Created" },
  { value: "FinalizedAt", label: "Finalized" },
  { value: "Status", label: "Status" },
  { value: "PeriodFrom", label: "Period from" },
  { value: "PeriodTo", label: "Period to" },
];

const ReconciliationReport = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState(sortByOptions[0]);
  const [sortOrder, setSortOrder] = useState({ value: "DESC", label: "DESC" });
  const [locations, setLocations] = useState([]);
  const [selectedCompID, setSelectedCompID] = useState(searchParams.get("compId") || "");
  const effectiveCompID = isMain ? Number(selectedCompID) || 0 : Number(CompID) || 0;

  const toInt = (value) => {
    const n = typeof value === "number" ? value : parseInt(value, 10);
    return Number.isFinite(n) ? n : null;
  };

  useEffect(() => {
    const baseCompId = toInt(CompID);
    if (baseCompId && (!selectedCompID || Number(selectedCompID) <= 0)) {
      setSelectedCompID(String(baseCompId));
    }
  }, [CompID, selectedCompID]);

  const fetchLocations = useCallback(async () => {
    if (!isMain || !CompID) return;
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, { params: { CompID } });
      const locationData = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(locationData) ? locationData : [];
      const baseCompId = toInt(CompID);
      if (!baseCompId) return;
      const parentName = profileData?.compName ?? profileData?.CompName ?? `Company ${baseCompId}`;
      const base = [{ value: baseCompId, label: parentName }];
      const branches = arr
        .filter((loc) => {
          const id = toInt(loc.CompID ?? loc.compID);
          const pid = toInt(loc.ParentComID ?? loc.parentComID);
          return id && pid === baseCompId && id !== baseCompId;
        })
        .map((loc) => {
          const id = toInt(loc.CompID ?? loc.compID);
          const name = String(loc.CompName ?? loc.compName ?? "").trim();
          return { value: id, label: name || `Branch ${id}` };
        })
        .filter((loc) => loc.value);

      setLocations([...base, ...branches]);
    } catch {
      setLocations([]);
    }
  }, [isMain, CompID, profileData]);

  const fetchList = useCallback(async () => {
    if (!effectiveCompID) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(APIs.GET_STOCK_RECONCILIATION_LIST, {
        params: {
          CompID: effectiveCompID,
          Status: statusFilter || undefined,
          Search: search?.trim() || "",
          SortBy: sortBy?.value ?? sortBy,
          SortOrder: sortOrder?.value ?? sortOrder,
          PageNumber: pageNumber,
          PageSize: pageSize?.value ?? pageSize,
        },
      });
      if (res.data?.status) {
        setRows(res.data.data || []);
        setTotalRecords(res.data.totalRecords ?? 0);
        setTotalPages(res.data.totalPages ?? 0);
      } else {
        showErrorToast(res.data?.message || "Failed to load list");
      }
    } catch (e) {
      showErrorToast("Failed to load reconciliation reports");
    } finally {
      setLoading(false);
    }
  }, [effectiveCompID, pageNumber, pageSize, search, sortBy, sortOrder, statusFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setSortBy(sortByOptions[0]);
    setSortOrder({ value: "DESC", label: "DESC" });
    setPageNumber(1);
  };

  const statusBadge = (status) => {
    const s = (status || "").toLowerCase();
    let cls = "bg-secondary";
    if (s === "finalized") cls = "bg-success";
    else if (s === "draft") cls = "bg-primary";
    else if (s === "inprogress") cls = "bg-info text-dark";
    else if (s === "cancelled") cls = "bg-dark";
    return <span className={`badge ${cls}`}>{status}</span>;
  };

  const displayName = (row) => {
    const n = row.notes ?? row.Notes;
    if (n && String(n).trim()) return String(n).trim();
    return `Reconciliation #${reconId(row)}`;
  };

  const handleDeleteReconciliation = async (id, status) => {
    if (!effectiveCompID) return;
    const st = String(status || "").toLowerCase();
    const label = st === "cancelled" ? "cancelled reconciliation" : "draft reconciliation";
    if (!window.confirm(`Delete this ${label} permanently?`)) return;
    setLoading(true);
    try {
      const res = await axiosInstance.delete(APIs.DELETE_STOCK_RECONCILIATION(id), {
        params: { CompID: effectiveCompID },
      });
      if (res.data?.status) {
        fetchList();
      } else {
        showErrorToast(res.data?.message || "Failed to delete reconciliation");
      }
    } catch (e) {
      showErrorToast(e.response?.data?.message || "Failed to delete reconciliation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">Inventory Management</li>
            <li className="breadcrumb-item active">Stock Adjustments</li>
          </ol>
          <h1 className="page-header mb-0">Stock Adjustments</h1>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn px-4"
            onClick={() => navigate(`/report/reconciliationReport/new?compId=${effectiveCompID || ""}`)}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
            onMouseEnter={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
              e.target.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
              e.target.style.transform = "translateY(0)";
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white" />
            Add reconciliation
          </button>
        </div>
      </div>

      <div className="mb-3 mt-2">
        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active">
              <div className="mb-4">
                <div
                  className="card filter-card"
                  style={{
                    position: "relative",
                    zIndex: 1,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    borderRadius: "12px",
                    border: "none",
                    boxShadow: `0 4px 20px ${themeColor}40`,
                  }}
                >
                  <div className="card-body p-3">
                    <div className="row g-3 align-items-center">
                      <div className="col-xl-4 col-lg-4 col-md-6">
                        <div className="search-input filter-control">
                          <input
                            type="text"
                            className="filter-input"
                            value={search}
                            onChange={(e) => {
                              setSearch(e.target.value);
                              setPageNumber(1);
                            }}
                            placeholder="Search by ID, status, name / notes..."
                            style={{
                              backgroundColor: "rgba(255, 255, 255, 0.9)",
                              border: "1px solid rgba(255, 255, 255, 0.3)",
                              color: "#333",
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-xl-2 col-lg-2 col-md-6">
                        {isMain && (
                          <select
                            className="form-control filter-input"
                            value={selectedCompID}
                            onChange={(e) => {
                              setSelectedCompID(e.target.value);
                              setPageNumber(1);
                            }}
                            style={{
                              backgroundColor: "rgba(255, 255, 255, 0.9)",
                              border: "1px solid rgba(255, 255, 255, 0.3)",
                              color: "#333",
                              height: "42px",
                            }}
                          >
                            {locations.map((loc) => (
                              <option key={loc.value} value={loc.value}>
                                {loc.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      <div className="col-xl-2 col-lg-2 col-md-6">
                        <select
                          className="form-control filter-input"
                          value={statusFilter}
                          onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPageNumber(1);
                          }}
                          style={{
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            border: "1px solid rgba(255, 255, 255, 0.3)",
                            color: "#333",
                            height: "42px",
                          }}
                        >
                          <option value="">All statuses</option>
                          <option value="Draft">Draft</option>
                          <option value="InProgress">In progress</option>
                          <option value="Finalized">Finalized</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                      </div>
                      <div className="col-xl-3 col-lg-3 col-md-6">
                        <select
                          className="form-control filter-input"
                          value={sortBy.value}
                          onChange={(e) => {
                            const sel = sortByOptions.find((o) => o.value === e.target.value);
                            setSortBy(sel || sortByOptions[0]);
                            setPageNumber(1);
                          }}
                          style={{
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            border: "1px solid rgba(255, 255, 255, 0.3)",
                            color: "#333",
                            height: "42px",
                          }}
                        >
                          {sortByOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-xl-1 col-lg-1 col-md-6">
                        <SortOrderDropDownSelection
                          defaultValue={sortOrder}
                          onChange={(val) => {
                            setSortOrder(val);
                            setPageNumber(1);
                          }}
                        />
                      </div>
                      <div className="col-xl-2 col-lg-2 col-md-12">
                        <button
                          type="button"
                          className="clear-btn"
                          onClick={clearFilters}
                          title="Clear filters"
                          style={{
                            backgroundColor: "rgba(0, 0, 0, 0.2)",
                            border: "1px solid rgba(255, 255, 255, 0.3)",
                            color: "white",
                            padding: "8px 16px",
                            borderRadius: "8px",
                            fontWeight: "600",
                          }}
                        >
                          <i className="fa fa-times me-1" />
                          Filter
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {rows.length === 0 && !loading ? (
                <div className="alert alert-info text-center mt-3">
                  <i className="fa fa-info-circle me-2" />
                  No reconciliations found
                </div>
              ) : (
                <>
                  <div
                    className="table-responsive table-container"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <table className="table table-hover" style={{ position: "relative" }}>
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10000,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          <th className="pt-2 pb-2">Name</th>
                          <th className="pt-2 pb-2">Period from</th>
                          <th className="pt-2 pb-2">Period to</th>
                          <th className="pt-2 pb-2 text-center">Lines</th>
                          <th className="pt-2 pb-2 text-center">Status</th>
                          <th className="pt-2 pb-2">Created</th>
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center">
                              No records
                            </td>
                          </tr>
                        ) : (
                          rows.map((r) => {
                            const id = reconId(r);
                            return (
                              <tr key={id}>
                                <td className="align-middle">
                                  <div style={{ fontWeight: 600, color: "#495057" }}>{displayName(r)}</div>
                                  <div style={{ fontSize: "12px", color: "#6c757d" }}>ID {id}</div>
                                </td>
                                <td className="align-middle">
                                  <div style={{ fontSize: "13px", color: "#495057" }}>
                                    {r.periodFromDateTime ? Moment(r.periodFromDateTime).format("DD MMM YYYY") : "—"}
                                  </div>
                                  <div style={{ fontSize: "12px", color: "#6c757d" }}>
                                    {r.periodFromDateTime ? Moment(r.periodFromDateTime).format("HH:mm") : ""}
                                  </div>
                                </td>
                                <td className="align-middle">
                                  <div style={{ fontSize: "13px", color: "#495057" }}>
                                    {r.periodToDateTime ? Moment(r.periodToDateTime).format("DD MMM YYYY") : "—"}
                                  </div>
                                  <div style={{ fontSize: "12px", color: "#6c757d" }}>
                                    {r.periodToDateTime ? Moment(r.periodToDateTime).format("HH:mm") : ""}
                                  </div>
                                </td>
                                <td className="align-middle text-center">{r.lineCount ?? r.LineCount ?? "—"}</td>
                                <td className="align-middle text-center">{statusBadge(r.status)}</td>
                                <td className="align-middle">
                                  <div style={{ fontSize: "13px", color: "#495057" }}>
                                    {r.createdAt ? Moment(r.createdAt).format("DD MMM YYYY") : "—"}
                                  </div>
                                  <div style={{ fontSize: "12px", color: "#6c757d" }}>
                                    {r.createdAt ? Moment(r.createdAt).format("HH:mm") : ""}
                                  </div>
                                </td>
                                <td className="align-middle text-center">
                                  <div className="d-inline-flex gap-2">
                                    <Link
                                      to={`/report/reconciliationReport/${id}?compId=${effectiveCompID}`}
                                      className="btn btn-sm"
                                      title="Open"
                                      style={{
                                        width: "32px",
                                        height: "32px",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: "6px",
                                        border: `1px solid ${themeColor}`,
                                        backgroundColor: "transparent",
                                        color: themeColor,
                                        textDecoration: "none",
                                      }}
                                    >
                                      <i className="fa fa-eye" style={{ fontSize: "12px" }} />
                                    </Link>
                                    {["draft", "cancelled"].includes(String(r.status || "").toLowerCase()) && (
                                      <button
                                        type="button"
                                        className="btn btn-sm"
                                        title="Delete reconciliation"
                                        onClick={() => handleDeleteReconciliation(id, r.status)}
                                        style={{
                                          width: "32px",
                                          height: "32px",
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          borderRadius: "6px",
                                          border: "1px solid #dc3545",
                                          backgroundColor: "transparent",
                                          color: "#dc3545",
                                        }}
                                      >
                                        <i className="fa fa-trash" style={{ fontSize: "12px" }} />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    pageNumber={pageNumber}
                    totalPages={totalPages}
                    setPageNumber={setPageNumber}
                    setPageSize={setPageSize}
                    themeColor={themeColor}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReconciliationReport;
